import { HgSupportedLanguage, hgNumber, hgPrice } from '@smg-real-estate/domus';

import { Range } from '@/models/HgRange';

const SPACE = ' ';
const BULLET_POINT = ' • ';

const LINE_BREAK_REGEX = /(?:\r\n|\r|\n)/g;
const OPEN_CLOSE_UL_BR_P_TAGS_REGEX = /<(\/?|\?)(p|ul|br)\s?\/?\s?>|<(\/)li\s?>|<li\s?\/\s?>/g;
const OPEN_LI_TAG_REGEX = /<\s?li\s?>/g;
const MULTIPLE_SPACES_REGEX = /\s{2,}/g;

export const truncateText = (text: string, trimLength = 100): string => {
  return text.length > trimLength ? `${text.slice(0, trimLength - 3).trim()}...` : text;
};

// convert new lines to spaces (prevents two words to stick together)
export const flattenHtmlDescription = (inputText: string): string =>
  inputText
    // regex to match <br> | <br/> | <br />
    .replace(/(?:<br>|<br(\s\/|\/)>|\r|\n)/g, SPACE)
    // regex to cleanup outstanding whitespaces
    .replace(MULTIPLE_SPACES_REGEX, SPACE)
    .trim();

export const cleanupMarkupTags = (inputText: string): string => {
  return inputText
    .replace(LINE_BREAK_REGEX, SPACE)
    .replace(OPEN_CLOSE_UL_BR_P_TAGS_REGEX, SPACE)
    .replace(OPEN_LI_TAG_REGEX, BULLET_POINT)
    .replace(MULTIPLE_SPACES_REGEX, SPACE)
    .trim();
};

export const convertNewLineCharToBrTag = (inputText?: string | null): string => {
  if (!inputText) {
    return '';
  }
  return inputText.replace(LINE_BREAK_REGEX, '<br />');
};

export const removeLinksFromDescription = (
  description: string,
  exceptions: string[] = [],
): string => {
  const exceptionPatterns = exceptions.map((exception) => new RegExp(exception, 'i'));

  return description.replace(/<a\s+href="([^"]+)"[^>]*>(.*?)<\/a>/gi, (match, url, text) => {
    const shouldKeep = exceptionPatterns.some((pattern) => pattern.test(url));
    return shouldKeep ? match : text;
  });
};

export const formatRangeBoundariesAsNumber = (
  numberRange: Range,
  language: HgSupportedLanguage,
): Range => {
  return formatRangeBoundaries(numberRange, language, 'hgNumber');
};

export const formatRangeBoundariesAsPrice = (
  priceRange: Range,
  language: HgSupportedLanguage,
): Range => {
  return formatRangeBoundaries(priceRange, language, 'hgPrice');
};

const formatRangeBoundaries = (
  priceRange: Range,
  language: HgSupportedLanguage,
  filter: string,
): Range => {
  const { from: fromNumber, to: toNumber } = priceRange;

  const helperFunctionName = filter === 'hgNumber' ? hgNumber : hgPrice;

  return {
    from: fromNumber ? helperFunctionName(Number(fromNumber), language) : '',
    to: toNumber ? helperFunctionName(Number(toNumber), language) : '',
  };
};

export const wrapUrlsInAnchors = (text: string): string => {
  const urlRegex =
    /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-a-zA-Z0-9()!@:%_+.~#?&//=]*/gm;

  return text.replace(
    urlRegex,
    (match: string) => `<a href='${match}' target='_blank' rel='noopener'>${match}</a>`,
  );
};
